/**
 * The Add Gear flow — a driver's safety equipment, tailored to what that gear
 * actually is.
 *
 * Gear is the same underlying item as a car part (`ownerType: 'driver'`,
 * `system: 'Safety'`), but the generic Add Part form is the wrong tool for it: a
 * racer does not think in "check conditions", they think "SFI 3.2A/5 suit,
 * recert due 2029". So this flow knows the standard drag-racing gear and the SFI
 * / Snell ratings each one carries, and asks only what matters for it — a spec
 * and a recert / replace date.
 *
 * Safety gear carries **no check condition**: the recert (or replace-by) date is
 * what matters, and it is what drives the 60/30-day reminders. Even the SFI 38.1
 * head-and-neck restraint, which carries no recert patch, is tracked by a
 * replace-by date rather than an inspection interval — a date is what a racer
 * keeps in their head, and one fewer concept is one fewer way to get it wrong.
 *
 * It writes a normal item, so the Driver screen, the timeline forecast and the
 * 60/30-day reminder job all pick it up with no special handling.
 */

import { useState } from 'react';
import { ShieldCheck, Trash2 } from 'lucide-react';

import type { CreateItemRequest, ItemDto } from '@svcdue/contracts';
import { addMonths } from '@svcdue/core';

import { ApiError } from '../api/client';
import { useCreateItem, useDeleteItem, useUpdateItem } from '../api/hooks';
import { errorMessage } from '../components/states';
import { Field, Opts, SheetHead } from '../components/form';
import { today } from '../lib/format';

interface GearType {
  key: string;
  /** The item name this produces (a suit appends its variant). */
  label: string;
  /** The SFI/Snell ratings this gear commonly carries. */
  specs: string[];
  /** Jacket & pants vs 1-piece, for a suit only. */
  variants?: string[];
  /** Typical recert cycle in months, to default the date. */
  recertMonths?: number;
  hint: string;
}

/**
 * The known gear, with real SFI / Snell ratings. The order is the order a tech
 * card is checked: suit, helmet, restraint, then the extremities.
 */
const GEAR_TYPES: GearType[] = [
  {
    key: 'suit',
    label: 'Racing suit',
    variants: ['1-piece', 'Jacket & pants'],
    specs: ['SFI 3.2A/1', 'SFI 3.2A/3', 'SFI 3.2A/5', 'SFI 3.2A/10', 'SFI 3.2A/15', 'SFI 3.2A/20'],
    recertMonths: 60,
    hint: 'SFI 3.2A suits carry a recertification patch and are recertified every 5 years.',
  },
  {
    key: 'jacket',
    label: 'Fire jacket',
    specs: ['SFI 3.2A/1', 'SFI 3.2A/3', 'SFI 3.2A/5'],
    recertMonths: 60,
    hint: 'A single-layer SFI 3.2A/1 jacket, or heavier — recertified every 5 years, like a suit.',
  },
  {
    key: 'helmet',
    label: 'Helmet',
    specs: ['Snell SA2020', 'Snell SA2015', 'Snell SA2010', 'SFI 31.1', 'SFI 41.1'],
    hint: 'Helmets are accepted by Snell rating year — set the date your sanctioning body stops accepting it.',
  },
  {
    key: 'hans',
    label: 'Head & neck restraint',
    specs: ['SFI 38.1'],
    hint: 'SFI 38.1 restraints carry no recert patch — set the replace-by date your tech uses, or when the tethers are due for renewal.',
  },
  {
    key: 'neckcollar',
    label: 'Neck collar',
    specs: ['SFI 3.3'],
    recertMonths: 60,
    hint: 'A foam SFI 3.3 collar — replace on your suit’s recert cycle, or sooner if it is damaged.',
  },
  {
    key: 'gloves',
    label: 'Gloves',
    specs: ['SFI 3.3/1', 'SFI 3.3/5', 'SFI 3.3/15', 'SFI 3.3/20'],
    recertMonths: 60,
    hint: 'SFI 3.3 gloves are recertified every 5 years.',
  },
  {
    key: 'shoes',
    label: 'Shoes',
    specs: ['SFI 3.3/5', 'SFI 3.3/15', 'SFI 3.3/20'],
    recertMonths: 60,
    hint: 'SFI 3.3 shoes are recertified every 5 years.',
  },
  {
    key: 'balaclava',
    label: 'Balaclava',
    specs: ['SFI 3.3'],
    recertMonths: 60,
    hint: 'Replace with your suit’s recert cycle, or sooner if it is damaged.',
  },
];

const CUSTOM = 'Enter your own';

export interface GearForm {
  type: string;
  variant: string;
  spec: string;
  specOther: string;
  /** Recert / replace date — every piece of gear is tracked by one. */
  expiry: string;
  brand: string;
  buy: string;
}

export const gearTypeFor = (key: string): GearType => GEAR_TYPES.find((g) => g.key === key) ?? GEAR_TYPES[0]!;

/** The item name a gear form produces — a suit carries its variant. */
export function gearName(type: GearType, variant: string): string {
  return type.variants ? `${type.label} — ${variant}` : type.label;
}

/**
 * Gear form → the item contract. Exported and pure so the mapping is checkable
 * without a sheet — this is where a wrong spec becomes a 422.
 *
 * Every piece of gear is a safety item with a recert / replace date and no check
 * conditions — the reminder job counts down to the date (§6).
 */
export function toRequest(form: GearForm, ownerId: string): CreateItemRequest {
  const type = gearTypeFor(form.type);
  const spec = (form.spec === CUSTOM ? form.specOther.trim() : form.spec) || null;
  const brand = form.brand.trim() || null;
  const buyUrl = form.buy.trim() || null;

  return {
    ownerType: 'driver' as const,
    ownerId,
    name: gearName(type, form.variant),
    partNumber: brand,
    system: 'Safety' as const,
    buyUrl,
    spec,
    isSafety: true,
    expirationDate: form.expiry || null,
    lastServicedAt: null,
    triggers: [],
  };
}

/** An existing gear item → the form, for editing. */
export function itemToForm(item: ItemDto): GearForm {
  // Match the stored name back to a known gear type (a suit's name is prefixed).
  const type = GEAR_TYPES.find((g) => item.name === g.label || item.name.startsWith(`${g.label} —`)) ?? GEAR_TYPES[0]!;
  const variant = type.variants ? (item.name.split(' — ')[1] ?? type.variants[0]!) : '';

  return {
    type: type.key,
    variant,
    spec: item.spec && type.specs.includes(item.spec) ? item.spec : item.spec ? CUSTOM : type.specs[0]!,
    specOther: item.spec && !type.specs.includes(item.spec) ? item.spec : '',
    expiry: item.expirationDate ?? '',
    brand: item.partNumber ?? '',
    buy: item.buyUrl ?? '',
  };
}

function emptyForm(): GearForm {
  const type = GEAR_TYPES[0]!;
  return {
    type: type.key,
    variant: type.variants?.[0] ?? '',
    spec: type.specs[0]!,
    specOther: '',
    // Default the recert date to one cycle out, so the common case is one tap.
    expiry: type.recertMonths ? addMonths(today(), type.recertMonths) : '',
    brand: '',
    buy: '',
  };
}

export function AddGear({
  garageId,
  driverId,
  driverName,
  item,
  onClose,
}: {
  garageId: string;
  driverId: string;
  driverName: string;
  item?: ItemDto;
  onClose: () => void;
}) {
  const editing = item !== undefined;
  const [f, setF] = useState<GearForm>(item ? itemToForm(item) : emptyForm());
  const set = <K extends keyof GearForm>(k: K, v: GearForm[K]) => setF((s) => ({ ...s, [k]: v }));

  const create = useCreateItem(garageId);
  const update = useUpdateItem(garageId, item?.id ?? '');
  const del = useDeleteItem(garageId);
  const save = editing ? update : create;
  const [confirmDelete, setConfirmDelete] = useState(false);

  const type = gearTypeFor(f.type);
  const busy = save.isPending || del.isPending;
  const valid = Boolean(f.expiry);

  /** Switching gear type resets the type-specific defaults (spec, date). */
  const pickType = (key: string) => {
    const t = gearTypeFor(key);
    setF((s) => ({
      ...s,
      type: key,
      variant: t.variants?.[0] ?? '',
      spec: t.specs[0]!,
      specOther: '',
      expiry: t.recertMonths ? addMonths(today(), t.recertMonths) : s.expiry,
    }));
  };

  const issue = (path: string) => (save.error instanceof ApiError ? save.error.issueFor(path) : undefined);
  const submit = () => {
    if (!valid || busy) return;
    const body = toRequest(f, driverId);
    if (editing) update.mutate(body, { onSuccess: onClose });
    else create.mutate(body, { onSuccess: onClose });
  };
  const remove = () => {
    if (!item || busy) return;
    del.mutate(item.id, { onSuccess: onClose });
  };

  return (
    <div className="sheet">
      <SheetHead
        title={editing ? 'Edit gear' : 'Add gear'}
        sub={driverName}
        onClose={onClose}
        saveLabel={save.isPending ? 'Saving…' : editing ? 'Save' : 'Add gear'}
        onSave={submit}
        saveDisabled={!valid || busy}
      />
      <div className="sheet-body">
        <div className="form-sec">
          <ShieldCheck size={12} /> Which gear
        </div>
        <Field label="Type">
          <Opts options={GEAR_TYPES.map((g) => g.label)} value={type.label} onChange={(label) => pickType(GEAR_TYPES.find((g) => g.label === label)!.key)} />
        </Field>

        {type.variants && (
          <Field label="Style">
            <Opts options={type.variants} value={f.variant} onChange={(v) => set('variant', v)} />
          </Field>
        )}

        <Field label="Rating" hint="SFI / Snell">
          <select className="select" value={f.spec} onChange={(e) => set('spec', e.target.value)}>
            {type.specs.map((s) => (
              <option key={s}>{s}</option>
            ))}
            <option>{CUSTOM}</option>
          </select>
        </Field>
        {f.spec === CUSTOM && (
          <Field label="Your rating">
            <input className="input" value={f.specOther} onChange={(e) => set('specOther', e.target.value)} placeholder="e.g., SFI 3.4/5" autoFocus />
          </Field>
        )}

        <p className="field-note gear-hint">{type.hint}</p>

        <div className="form-sec">Recertification</div>
        <Field label="Recert / replace by" hint="drives the 60 & 30-day email notices">
          <input className="input" type="date" value={f.expiry} onChange={(e) => set('expiry', e.target.value)} />
          {issue('expirationDate') && <div className="field-err">{issue('expirationDate')}</div>}
        </Field>

        <div className="form-sec">Details <span className="sec-hint">optional</span></div>
        <div className="field-grid-2">
          <Field label="Brand / model">
            <input className="input" value={f.brand} onChange={(e) => set('brand', e.target.value)} placeholder="e.g., Simpson, HANS" />
          </Field>
          <Field label="Reorder URL">
            <input className="input" type="url" inputMode="url" value={f.buy} onChange={(e) => set('buy', e.target.value)} placeholder="https://…" />
          </Field>
        </div>

        {save.isError && !issue('expirationDate') && <div className="field-err">{errorMessage(save.error)}</div>}

        <button className="sheet-save-lg" onClick={submit} disabled={!valid || busy}>
          {save.isPending ? 'Saving…' : editing ? 'Save changes' : `Add gear to ${driverName}`}
        </button>

        {editing && (
          <div className="danger-zone">
            {del.isError && <div className="field-err">{errorMessage(del.error)}</div>}
            {confirmDelete ? (
              <div className="danger-confirm">
                <span>Remove this gear from {driverName}?</span>
                <div className="danger-actions">
                  <button className="act" onClick={() => setConfirmDelete(false)} disabled={busy}>
                    Keep
                  </button>
                  <button className="act act-danger" onClick={remove} disabled={busy}>
                    {del.isPending ? 'Removing…' : 'Remove gear'}
                  </button>
                </div>
              </div>
            ) : (
              <button className="danger-link" onClick={() => setConfirmDelete(true)} disabled={busy}>
                <Trash2 size={13} /> Remove this gear
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
